import { useCallback, useEffect, useMemo, useState } from "react";
import { demoGames, demoGoals, demoOpponents, demoPlayers, demoServes } from "../data/demoData";
import { supabase } from "../supabase";
import type { DataIssue, Game, Goals, Opponent, Player, Serve } from "../types";
import { defaultGoals } from "../utils/analytics";

type PlayerRow = {
  id: string;
  first_name: string;
  last_name: string;
  number: number;
  position: string;
  avatar_url: string | null;
  birth_date: string | null;
  height_cm: number | null;
  weight_kg: number | null;
  eligibility_years: number | null;
};

type ServeRow = {
  id: string;
  player_id: string | null;
  outcome: "in_play" | "ace" | "miss";
  kind: "float" | "jump_float" | "topspin" | "other";
  speed_kmh: number;
  recorded_at: string;
  // Present once games_and_reception.sql has run
  game_id?: string | null;
  receiver_number?: number | null;
  reception_grade?: number | null;
  reception_scale_min?: number | null;
  reception_scale_max?: number | null;
};

type GameRow = {
  id: string;
  opponent_id: string;
  played_on: string;
  season: string | null;
};

// Older databases without the games migration: treat as "no games yet"
function isMissingTable(error: { code?: string } | null) {
  return error?.code === "42P01" || error?.code === "PGRST205";
}

const outcomeToState: Record<ServeRow["outcome"], Serve["state"]> = {
  miss: 1,
  in_play: 2,
  ace: 3
};

const kindToLegacyType: Record<ServeRow["kind"], string> = {
  float: "float",
  jump_float: "hyb",
  topspin: "spin",
  other: "other"
};

function toIssue(error: { code?: string; message: string }): DataIssue {
  if (error.code === "42501") {
    return {
      code: "permission-denied",
      title: "Permission denied",
      message: "Your account is signed in, but Supabase row-level security blocked this dashboard data."
    };
  }
  if (/fetch|network|unreachable/i.test(error.message)) {
    return {
      code: "unavailable",
      title: "Connection unavailable",
      message: "Supabase is currently unreachable. The dashboard will reconnect automatically."
    };
  }
  if (error.code === "PGRST301") {
    return { code: "unauthenticated", title: "Session expired", message: "Sign in again to reload team data." };
  }
  return { code: "unknown", title: "Data error", message: error.message };
}

export function useSupabaseData(uid: string | undefined) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [serves, setServes] = useState<Serve[]>([]);
  const [goals, setGoals] = useState<Goals>(defaultGoals);
  const [opponents, setOpponents] = useState<Opponent[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<DataIssue | null>(null);

  const load = useCallback(async () => {
    if (!uid || uid === "demo") return;
    setIssue(null);
    // Same org-scoped RLS as the mobile app — no separate dashboard_access gate.
    const [playersResult, servesResult, goalsResult, opponentsResult, gamesResult] = await Promise.all([
      supabase.from("players").select("*").order("number"),
      supabase.from("serves").select("*").order("recorded_at", { ascending: false }),
      supabase.from("goals").select("*").eq("user_id", uid).maybeSingle(),
      supabase.from("opponents").select("id,name").order("name"),
      supabase.from("games").select("id,opponent_id,played_on,season").order("played_on", { ascending: false })
    ]);
    const optionalError = [opponentsResult.error, gamesResult.error].find((e) => e && !isMissingTable(e)) ?? null;
    const error = playersResult.error ?? servesResult.error ?? goalsResult.error ?? optionalError;
    if (error) {
      setIssue(toIssue(error));
      setPlayers([]);
      setServes([]);
      setLoading(false);
      return;
    }

    setPlayers(((playersResult.data ?? []) as PlayerRow[]).map((row) => ({
      id: row.id,
      firstName: row.first_name,
      lastName: row.last_name,
      number: Number(row.number ?? 0),
      position: row.position || "Player",
      image: row.avatar_url ?? "",
      dateOfBirth: row.birth_date ?? "",
      height: row.height_cm == null ? "" : Math.round(row.height_cm),
      weight: row.weight_kg == null ? "" : Math.round(row.weight_kg),
      yearsOfEligibility: row.eligibility_years ?? ""
    })));
    setServes(((servesResult.data ?? []) as ServeRow[]).map((row) => ({
      id: row.id,
      playerId: row.player_id ?? "",
      state: outcomeToState[row.outcome] ?? 2,
      type: kindToLegacyType[row.kind] ?? row.kind,
      speed: Number(row.speed_kmh ?? 0),
      date: row.recorded_at ? new Date(row.recorded_at) : null,
      gameId: row.game_id ?? null,
      receiverNumber: row.receiver_number ?? null,
      receptionGrade: row.reception_grade ?? null,
      receptionScale: row.reception_scale_min != null && row.reception_scale_max != null
        ? [row.reception_scale_min, row.reception_scale_max]
        : null
    })));
    setOpponents(((opponentsResult.data ?? []) as Opponent[]).map((row) => ({ id: row.id, name: row.name })));
    setGames(((gamesResult.data ?? []) as GameRow[]).map((row) => ({
      id: row.id,
      opponentId: row.opponent_id,
      playedOn: row.played_on,
      season: row.season ?? ""
    })));

    if (goalsResult.data) {
      const row = goalsResult.data;
      setGoals({
        avgSpeedFloat: Number(row.avg_speed_float),
        avgSpeedHybrid: Number(row.avg_speed_hybrid),
        avgSpeedSpin: Number(row.avg_speed_spin),
        effiencyFloat: Number(row.efficiency_float),
        effiencyHybrid: Number(row.efficiency_hybrid),
        effiencySpin: Number(row.efficiency_spin)
      });
    } else {
      const { error: insertError } = await supabase.from("goals").insert({
        user_id: uid,
        avg_speed_float: defaultGoals.avgSpeedFloat,
        avg_speed_hybrid: defaultGoals.avgSpeedHybrid,
        avg_speed_spin: defaultGoals.avgSpeedSpin,
        efficiency_float: defaultGoals.effiencyFloat,
        efficiency_hybrid: defaultGoals.effiencyHybrid,
        efficiency_spin: defaultGoals.effiencySpin
      });
      if (insertError) setIssue(toIssue(insertError));
      setGoals(defaultGoals);
    }
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    if (uid === "demo") {
      setPlayers(demoPlayers);
      setServes(demoServes);
      setGoals(demoGoals);
      setOpponents(demoOpponents);
      setGames(demoGames);
      setIssue(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    void load();
    const channel = supabase
      .channel(`dashboard-${uid}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "players", filter: `user_id=eq.${uid}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "serves", filter: `user_id=eq.${uid}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "goals", filter: `user_id=eq.${uid}` }, load)
      // Workspace-scoped tables: row-level security decides which changes arrive
      .on("postgres_changes", { event: "*", schema: "public", table: "opponents" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "games" }, load)
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [load, uid]);

  return useMemo(
    () => ({ players, serves, goals, opponents, games, goalDocId: uid ?? "", loading, issue, refresh: load }),
    [players, serves, goals, opponents, games, uid, loading, issue, load]
  );
}
